import React, { useEffect, useRef, useState } from 'react';
import { Badge } from 'antd';
import {
  stepContainer,
  stepItem,
  stepNumber,
  stepNumberCompleted,
  stepNumberActive,
  stepNumberUpcoming,
  stepLine,
  cardContainerCompleted,
  cardContainerActive,
  cardContainerUpcoming,
  cardIconCompleted,
  cardIconActive,
  cardIconUpcoming,
  cardContent,
  ribbonWrapper,
  cardTitle,
} from './stepStyles';

interface IStep {
  id: number;
  title: string;
  icon: string;
  status: 'completed' | 'active' | 'upcoming';
}

const steps: IStep[] = [
  {
    id: 1,
    title: 'Initial Project and Property Information',
    icon: '/public/icons/plan-steps/step1-project-info.svg',
    status: 'completed',
  },
  {
    id: 2,
    title: 'As Built Site Plan',
    icon: '/public/icons/plan-steps/step2-site-plan.svg',
    status: 'active',
  },
  {
    id: 3,
    title: 'Feasibility & Preliminary design sign-off',
    icon: '/public/icons/plan-steps/step3-feasibility.svg',
    status: 'upcoming',
  },
  {
    id: 4,
    title: 'Construction Drawings',
    icon: '/public/icons/plan-steps/step4-drawings.svg',
    status: 'upcoming',
  },
  {
    id: 5,
    title: 'Submission for Building Permit',
    icon: '/public/icons/plan-steps/step5-permit.svg',
    status: 'upcoming',
  },
  {
    id: 6,
    title: 'Construction',
    icon: '/public/icons/plan-steps/step6-construction.svg',
    status: 'upcoming',
  },
];

const PlanStepsView: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getCardContainerClass = (status: string) => {
    switch (status) {
      case 'completed':
        return cardContainerCompleted;
      case 'active':
        return cardContainerActive;
      default:
        return cardContainerUpcoming;
    }
  };

  const getCardIconClass = (status: string) => {
    switch (status) {
      case 'completed':
        return cardIconCompleted;
      case 'active':
        return cardIconActive;
      default:
        return cardIconUpcoming;
    }
  };

  const getStepNumberClass = (status: string) => {
    switch (status) {
      case 'completed':
        return stepNumberCompleted;
      case 'active':
        return stepNumberActive;
      default:
        return stepNumberUpcoming;
    }
  };

  const renderStep = (step: IStep, index: number) => (
    <div
      key={step.id}
      className={`${stepItem} ${isVisible ? 'animate' : ''}`}
      style={{
        transitionDelay: isVisible ? `${index * 0.35}s` : '0s',
      }}>
      <div className={`${stepNumber} ${getStepNumberClass(step.status)}`}>{step.id}</div>

      {step.status === 'completed' ? (
        <div className={ribbonWrapper}>
          <Badge.Ribbon text="Completed" color="green">
            <div className={getCardContainerClass(step.status)}>
              <div className={getCardIconClass(step.status)}>
                <img
                  src={step.icon}
                  alt={`Step ${step.id} icon`}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
              <div className={cardContent}>
                <p className={cardTitle}>{step.title}</p>
              </div>
            </div>
          </Badge.Ribbon>
        </div>
      ) : (
        <div className={getCardContainerClass(step.status)}>
          <div className={getCardIconClass(step.status)}>
            <img
              src={step.icon}
              alt={`Step ${step.id} icon`}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
          <div className={cardContent}>
            <p className={cardTitle}>{step.title}</p>
          </div>
        </div>
      )}

      {index < steps.length - 1 && <div className={`${stepLine} step-line`} />}
    </div>
  );

  return (
    <div ref={containerRef} className={stepContainer}>
      {steps.map((step, index) => renderStep(step, index))}
    </div>
  );
};

export { PlanStepsView };
export default PlanStepsView;
